/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Inbox: the connected Gmail or Outlook mailbox inside Pearl.
 *
 * This file owns state and API calls; the panes live in ./components. The
 * mailbox connection has four states, kept apart on purpose:
 *   loading        -> skeleton frame
 *   not_connected  -> InboxConnectEmptyState (connect Gmail/Outlook in place)
 *   error          -> InboxLoadError (GET /email-settings failed; retry)
 *   connected      -> sidebar, list, reader/compose
 * A failed message fetch is none of these: it shows inline in the list, with
 * a Reconnect action when the provider token has expired. Previously all of
 * them shared one flag and showed "Email Configuration Required".
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import CryptoJS from 'crypto-js';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { useAuth } from 'auth/Auth';
import { useFeatureLicense } from 'hooks/useFeatureLicense';
import useEmailProviderConnect, { providerForAccount } from 'hooks/useEmailProviderConnect';
import { useMaterialUIController } from 'context';
import { APP_SCALE_MIN_WIDTH, scaledVh } from 'assets/theme/base/appScale';
import { frontendLogger } from 'utils/logger';

import MDBox from 'components/MDBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

import { createInboxTheme } from './inboxTheme';
import { toFolderModels } from './folderIcons';
import { bareAddresses, replyAllRecipients } from './emailFormat';
import InboxConnectEmptyState from './components/InboxConnectEmptyState';
import InboxLoadError from './components/InboxLoadError';
import InboxSidebar, { AccountBadge } from './components/InboxSidebar';
import EmailListPane, { EmailListSkeleton } from './components/EmailListPane';
import EmailReaderPane, { ReaderPlaceholder } from './components/EmailReaderPane';
import ComposePane from './components/ComposePane';

const CACHE_TTL_MS = 5 * 60 * 1000;
const PAGE_SIZE = 10;
const EMPTY_DRAFT = { to: '', cc: '', bcc: '', subject: '', content: '' };

// Height of everything above the frame: navbar, page header and padding.
const FRAME_OFFSET = '176px';
const frameHeightSx = {
  height: `calc(100vh - ${FRAME_OFFSET})`,
  [`@media screen and (min-width: ${APP_SCALE_MIN_WIDTH}px)`]: { height: scaledVh(FRAME_OFFSET) },
};

const initialMailboxFor = (provider) => (provider === 'Outlook' ? 'Inbox' : 'INBOX');

// Recipient fields carry bare addresses: the providers send headers such as
// `"Doe, Jane" <jane@x.com>`, which the compose pane would reject.
const splitList = (value) => bareAddresses(value);
const joinList = (value) => bareAddresses(value).join(', ');

function listErrorFrom(error) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.error_code === 'EMAIL_PROVIDER_AUTH_FAILED') {
    return { message: detail.message || 'Your mailbox connection expired. Reconnect to keep reading mail.', reconnect: true };
  }
  return { message: (typeof detail === 'string' && detail) || 'Messages could not be loaded.', reconnect: false };
}

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
};

function FrameSkeleton() {
  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <Box sx={{ width: 232, p: 2, borderRight: 1, borderColor: 'divider', display: { xs: 'none', md: 'block' } }}>
        <Skeleton variant="rounded" height={44} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={36} sx={{ mb: 2 }} />
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} variant="text" height={32} />
        ))}
      </Box>
      <Box sx={{ width: { xs: '100%', md: 360 }, borderRight: 1, borderColor: 'divider' }}>
        <EmailListSkeleton count={8} />
      </Box>
      <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
    </Box>
  );
}

function EmailInbox() {
  const { user, axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { features, loading: licenseLoading } = useFeatureLicense();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useMemo(() => createInboxTheme(darkMode), [darkMode]);
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  // Connection
  const [configState, setConfigState] = useState('loading');
  const [configError, setConfigError] = useState('');
  const [configChecking, setConfigChecking] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);

  // Folders and messages
  const [rawFolders, setRawFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [currentMailbox, setCurrentMailbox] = useState('INBOX');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [listError, setListError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [busy, setBusy] = useState(false);

  // Compose
  const [composing, setComposing] = useState(false);
  const [composeTitle, setComposeTitle] = useState('New message');
  const [newEmail, setNewEmail] = useState(EMPTY_DRAFT);
  const [draftId, setDraftId] = useState(null);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [signature, setSignature] = useState('');

  // AI drafting
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiContext, setAiContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const searchTimeout = useRef(null);
  const listRequestRef = useRef(0);

  const provider = emailConfig?.email_provider;
  const folders = useMemo(() => toFolderModels(rawFolders, provider), [rawFolders, provider]);
  const currentFolder = folders.find((folder) => folder.param === currentMailbox) || null;
  const folderTitle = currentFolder?.label || (currentMailbox.toLowerCase() === 'inbox' ? 'Inbox' : currentMailbox);
  // Compose rules key on the folder names the backend maps (see folderIcons.js).
  // Gmail's label is DRAFT, so key on the folder kind where there is one.
  const isDraftsFolder = currentFolder ? currentFolder.kind === 'drafts' : currentMailbox.toLowerCase() === 'drafts';
  const isSentFolder = currentFolder ? currentFolder.kind === 'sent' : currentMailbox.toLowerCase() === 'sent';

  // ---------------------------------------------------------------------
  // Encrypted per-user cache of each folder's first page (5 minutes)
  // ---------------------------------------------------------------------

  const cacheKey = useCallback((folder) => `${user?.id}_${folder}_emails`, [user?.id]);

  const getCachedEmails = useCallback(
    (folder) => {
      if (!user?.id) return null;
      try {
        const encrypted = localStorage.getItem(cacheKey(folder));
        if (!encrypted) return null;
        const bytes = CryptoJS.AES.decrypt(encrypted, `${user.id}_email_cache`);
        const cache = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
        if (!cache?.timestamp || Date.now() - cache.timestamp > CACHE_TTL_MS) {
          localStorage.removeItem(cacheKey(folder));
          return null;
        }
        return cache;
      } catch {
        return null;
      }
    },
    [cacheKey, user?.id]
  );

  const setCachedEmails = useCallback(
    (folder, list, cursor) => {
      if (!user?.id) return;
      try {
        const payload = JSON.stringify({ emails: list, nextCursor: cursor || null, timestamp: Date.now() });
        localStorage.setItem(cacheKey(folder), CryptoJS.AES.encrypt(payload, `${user.id}_email_cache`).toString());
      } catch {
        // Storage full or blocked: the cache is only a speed-up.
      }
    },
    [cacheKey, user?.id]
  );

  const clearCachedEmails = useCallback(
    (folder) => {
      try {
        localStorage.removeItem(cacheKey(folder));
      } catch {
        // ignore
      }
    },
    [cacheKey]
  );

  // ---------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------

  const checkEmailConfiguration = useCallback(
    async ({ background = false } = {}) => {
      setConfigChecking(true);
      try {
        const response = await axiosInstance.get('/email-settings');
        if (response.data && response.data.email_provider) {
          setEmailConfig((prev) => {
            if (!prev || prev.email_provider !== response.data.email_provider) {
              setCurrentMailbox(initialMailboxFor(response.data.email_provider));
            }
            return response.data;
          });
          setConfigState('connected');
        } else {
          // JSON null: no mailbox connected yet. Not an error.
          setEmailConfig(null);
          setConfigState('not_connected');
        }
        setConfigError('');
      } catch (error) {
        // A background re-check (after a popup closes) must not knock a
        // working view into the error state.
        if (!background) {
          setConfigError(errorText(error, ''));
          setConfigState('error');
        }
      } finally {
        setConfigChecking(false);
      }
    },
    [axiosInstance]
  );

  const fetchFolders = useCallback(async () => {
    setFoldersLoading(true);
    try {
      const response = await axiosInstance.get('/emails/folders');
      setRawFolders(response.data?.folders || []);
    } catch (error) {
      console.error('Error fetching folders:', error);
      notify('Email folders could not be loaded', 'error');
    } finally {
      setFoldersLoading(false);
    }
  }, [axiosInstance, notify]);

  const fetchEmails = useCallback(
    async (folder, { cursor = null, search = '', useCache = true } = {}) => {
      const requestId = listRequestRef.current + 1;
      listRequestRef.current = requestId;
      setListError(null);

      if (!cursor && !search && useCache) {
        const cached = getCachedEmails(folder);
        if (cached) {
          setEmails(cached.emails || []);
          setNextCursor(cached.nextCursor || null);
          setLoading(false);
          return;
        }
      }

      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setEmails([]);
        setNextCursor(null);
      }

      try {
        const response = await axiosInstance.get(`/emails/${encodeURIComponent(folder)}`, {
          params: { cursor: cursor || undefined, limit: PAGE_SIZE, search: search || undefined },
        });
        if (listRequestRef.current !== requestId) return; // a newer folder/search won
        const { emails: page = [], next_cursor: next = null, error } = response.data || {};
        if (error && !page.length && !cursor) {
          setListError({ message: 'Your mail provider did not return this folder. Try again in a moment.', reconnect: false });
        }
        setNextCursor(next);
        if (cursor) {
          setEmails((prev) => {
            const seen = new Set(prev.map((email) => email.id));
            return [...prev, ...page.filter((email) => !seen.has(email.id))];
          });
        } else {
          setEmails(page);
          if (!search && !error) setCachedEmails(folder, page, next);
        }
      } catch (error) {
        if (listRequestRef.current !== requestId) return;
        console.error('Error fetching emails:', error);
        setListError(listErrorFrom(error));
      } finally {
        if (listRequestRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [axiosInstance, getCachedEmails, setCachedEmails]
  );

  const fetchSignature = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/email-settings/signature');
      const next = response.data?.signature || '';
      setSignature(next);
      return next;
    } catch (error) {
      setSignature('');
      return '';
    }
  }, [axiosInstance]);

  const ensureSignature = async () => signature || fetchSignature();

  useEffect(() => {
    if (isAuthReady && isAuthenticated) checkEmailConfiguration();
  }, [isAuthReady, isAuthenticated, checkEmailConfiguration]);

  useEffect(() => {
    if (configState === 'connected') {
      fetchFolders();
      fetchSignature();
    }
  }, [configState, provider, fetchFolders, fetchSignature]);

  useEffect(() => {
    if (configState === 'connected') fetchEmails(currentMailbox, { search: searchQuery });
    // Search changes are debounced in handleSearch; this effect is for folder
    // and connection changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configState, provider, currentMailbox, fetchEmails]);

  // ---------------------------------------------------------------------
  // Connecting a mailbox (empty state and Reconnect)
  // ---------------------------------------------------------------------

  const connector = useEmailProviderConnect({
    onConnected: (connectedProvider) => {
      notify(`${connectedProvider === 'outlook' ? 'Outlook' : connectedProvider === 'gmail' ? 'Gmail' : 'Mailbox'} connected`);
      try {
        Object.keys(localStorage)
          .filter((key) => key.startsWith(`${user?.id}_`) && key.endsWith('_emails'))
          .forEach((key) => localStorage.removeItem(key));
      } catch {
        // ignore
      }
      setListError(null);
      const wasConnected = configState === 'connected';
      checkEmailConfiguration({ background: true }).then(() => {
        // A first connection loads through the effects above; a reconnect of
        // the same provider changes no effect dependency, so reload here.
        if (wasConnected) {
          fetchFolders();
          fetchEmails(currentMailbox, { useCache: false });
        }
      });
    },
    // The empty state shows connector.error itself; a Reconnect from the list
    // error has nowhere else to report, so use the snackbar there.
    onFailed: (message) => {
      if (configState === 'connected') notify(message || 'Could not reconnect the mailbox', 'error');
    },
    onPopupClosed: () => checkEmailConfiguration({ background: true }),
  });

  const openEmailSettings = () => navigate('/profile?tab=email');

  const handleReconnect = () => {
    const entry = providerForAccount(provider);
    if (entry) connector.connect(entry.key);
    else openEmailSettings();
  };

  // ---------------------------------------------------------------------
  // Folder, list and reading
  // ---------------------------------------------------------------------

  const adjustFolderUnread = (delta) => {
    setRawFolders((prev) =>
      prev.map((folder) => {
        const param = toFolderModels([folder], provider)[0]?.param;
        if (param !== currentMailbox) return folder;
        if (provider === 'Outlook') return { ...folder, unread_count: Math.max(0, (folder.unread_count || 0) + delta) };
        return { ...folder, messagesUnread: Math.max(0, (folder.messagesUnread || 0) + delta) };
      })
    );
  };

  const handleSelectFolder = (folder) => {
    setSelectedEmail(null);
    setComposing(false);
    setSearchQuery('');
    if (folder.param === currentMailbox) {
      fetchEmails(folder.param, { useCache: false });
    } else {
      setCurrentMailbox(folder.param);
    }
  };

  const handleRefresh = () => {
    clearCachedEmails(currentMailbox);
    fetchEmails(currentMailbox, { search: searchQuery, useCache: false });
    fetchFolders();
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchEmails(currentMailbox, { search: query }), 500);
  };

  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  const handleLoadMore = useCallback(() => {
    if (nextCursor && !loadingMore) fetchEmails(currentMailbox, { cursor: nextCursor, search: searchQuery });
  }, [nextCursor, loadingMore, fetchEmails, currentMailbox, searchQuery]);

  const markAsRead = async (emailId) => {
    try {
      await axiosInstance.post(`/emails/${encodeURIComponent(emailId)}/mark-read`);
      setEmails((prev) => prev.map((email) => (email.id === emailId ? { ...email, read: true } : email)));
      adjustFolderUnread(-1);
      clearCachedEmails(currentMailbox);
    } catch (error) {
      console.error('Error marking email as read:', error);
    }
  };

  const markAsUnread = async (emailId) => {
    setBusy(true);
    try {
      await axiosInstance.post(`/emails/${encodeURIComponent(emailId)}/mark-unread`);
      setEmails((prev) => prev.map((email) => (email.id === emailId ? { ...email, read: false } : email)));
      adjustFolderUnread(1);
      clearCachedEmails(currentMailbox);
      setSelectedEmail(null);
      notify('Marked as unread');
    } catch (error) {
      notify('Failed to mark as unread', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteEmail = async (emailId) => {
    setBusy(true);
    try {
      await axiosInstance.delete(`/emails/${encodeURIComponent(emailId)}`);
      setEmails((prev) => prev.filter((email) => email.id !== emailId));
      setSelectedEmail((prev) => (prev && prev.id === emailId ? null : prev));
      clearCachedEmails(currentMailbox);
      notify('Email deleted');
      await frontendLogger.logDelete('Email', {
        id: emailId,
        action: 'delete',
        user: user.username,
        user_id: user.id,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error deleting email:', error);
      notify('Failed to delete email', 'error');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------------
  // Compose, reply, forward, drafts
  // ---------------------------------------------------------------------

  const resetComposeFields = ({ title, draft, cc = false, bcc = false, id = null, files = [] }) => {
    setComposeTitle(title);
    setNewEmail(draft);
    setShowCc(cc);
    setShowBcc(bcc);
    setDraftId(id);
    setAttachments(files);
    setAiError('');
    setComposing(true);
  };

  const handleEditDraft = (draftEmail) => {
    setSelectedEmail(null);
    resetComposeFields({
      title: 'Edit draft',
      draft: {
        to: joinList(draftEmail.to),
        cc: joinList(draftEmail.cc),
        bcc: joinList(draftEmail.bcc),
        subject: draftEmail.subject || '',
        content: draftEmail.body || '',
      },
      cc: !!draftEmail.cc,
      bcc: !!draftEmail.bcc,
      id: draftEmail.id,
      files: draftEmail.attachments || [],
    });
  };

  const handleEmailSelect = (email) => {
    if (isDraftsFolder && provider === 'Gmail') {
      handleEditDraft(email);
      return;
    }
    setSelectedEmail(email);
    setComposing(false);
    if (!email.read) markAsRead(email.id);
  };

  const handleCompose = async () => {
    setSelectedEmail(null);
    resetComposeFields({ title: 'New message', draft: { ...EMPTY_DRAFT } });
    const sig = await ensureSignature();
    setNewEmail((prev) => (prev.content ? prev : { ...prev, content: `<br/><br/>${sig || ''}` }));
  };

  const quotedHeader = (email) =>
    `On ${new Date(email.date).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}, ${email.from} wrote:`;

  const replySubject = (subject) => (String(subject || '').startsWith('Re:') ? subject : `Re: ${subject || ''}`);

  const handleReply = async () => {
    const email = selectedEmail;
    const sig = await ensureSignature();
    const replyTo = (isSentFolder ? splitList(email.to) : splitList(email.from))[0] || '';
    resetComposeFields({
      title: 'Reply',
      draft: {
        to: replyTo,
        cc: '',
        bcc: '',
        subject: replySubject(email.subject),
        content: `<br/>${sig || ''}<br/><br/>${quotedHeader(email)}<br/>${email.body}`,
      },
    });
  };

  // Reply all: everyone on To and Cc except this mailbox; Bcc recipients of
  // the original (visible in Sent) are copied, as before.
  const handleReplyAll = async () => {
    const email = selectedEmail;
    const sig = await ensureSignature();
    const { to, cc } = replyAllRecipients(email, {
      self: [user?.email, emailConfig?.email_address],
      fromSent: isSentFolder,
    });

    resetComposeFields({
      title: 'Reply all',
      draft: {
        to: to.join(', '),
        cc: cc.join(', '),
        bcc: '',
        subject: replySubject(email.subject),
        content: `<br/>${sig || ''}<br/><br/>${quotedHeader(email)}<br/>${email.body}`,
      },
      cc: cc.length > 0,
    });
  };

  const handleForward = async () => {
    const email = selectedEmail;
    const sig = await ensureSignature();
    const forwarded =
      '<br/><br/>-------- Forwarded Message --------<br/>' +
      `From: ${email.from}<br/>` +
      `Date: ${new Date(email.date).toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })}<br/>` +
      `Subject: ${email.subject}<br/>` +
      `To: ${email.to}<br/>` +
      (email.cc ? `Cc: ${email.cc}<br/>` : '') +
      (email.bcc ? `Bcc: ${email.bcc}<br/>` : '') +
      `<br/>${email.body}`;
    resetComposeFields({
      title: 'Forward',
      draft: {
        to: '',
        cc: '',
        bcc: '',
        subject: String(email.subject || '').startsWith('Fwd:') ? email.subject : `Fwd: ${email.subject || ''}`,
        content: `<br/>${sig || ''}${forwarded}`,
      },
    });
  };

  const handleDiscard = () => {
    setComposing(false);
    setDraftId(null);
    setNewEmail(EMPTY_DRAFT);
    setAttachments([]);
  };

  const handleSaveDraft = async (overrideDraftId = draftId) => {
    setSavingDraft(true);
    try {
      const payload = {
        draft_id: overrideDraftId,
        to: splitList(newEmail.to),
        cc: splitList(newEmail.cc),
        bcc: splitList(newEmail.bcc),
        subject: newEmail.subject,
        body: newEmail.content,
        is_html: true,
        attachments,
      };
      const response = await axiosInstance.post('/emails/draft', payload);
      setDraftId(response.data.draft_id);
      notify('Draft saved');
      await frontendLogger.logUpdate('Email', {
        id: response.data.draft_id,
        action: 'update',
        subject: payload.subject,
        user: user.username,
        user_id: user.id,
        timestamp: new Date().toISOString(),
      });
      if (isDraftsFolder) {
        clearCachedEmails(currentMailbox);
        fetchEmails(currentMailbox, { useCache: false });
      }
    } catch (error) {
      notify(errorText(error, 'Failed to save draft'), 'error');
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    try {
      const emailToSend = {
        to: joinList(newEmail.to),
        cc: joinList(newEmail.cc),
        bcc: joinList(newEmail.bcc),
        subject: newEmail.subject,
        content: newEmail.content,
        attachments: attachments.map((att) => ({
          filename: att.filename,
          content: att.content,
          content_type: att.content_type,
          size: att.size,
        })),
      };
      await axiosInstance.post('/emails/send', emailToSend);
      notify('Email sent');
      await frontendLogger.logCreate('Email', {
        to: emailToSend.to,
        subject: emailToSend.subject,
        action: 'create',
        user: user.username,
        user_id: user.id,
        timestamp: new Date().toISOString(),
      });
      setNewEmail(EMPTY_DRAFT);
      setAttachments([]);
      setComposing(false);
      if (isSentFolder) clearCachedEmails(currentMailbox);
    } catch (error) {
      console.error('Error sending email:', error);
      notify(errorText(error, 'Failed to send email'), 'error');
    } finally {
      setSending(false);
    }
  };

  const searchRecipients = useMemo(
    () =>
      debounce(async (query) => {
        try {
          setRecipientsLoading(true);
          const response = await axiosInstance.get('/email-recipients/search', { params: { query } });
          setRecipientOptions(response.data?.results || []);
        } catch (error) {
          setRecipientOptions([]);
        } finally {
          setRecipientsLoading(false);
        }
      }, 300),
    [axiosInstance]
  );

  useEffect(() => () => searchRecipients.cancel(), [searchRecipients]);

  const handleRecipientSearch = (value) => {
    if (value && value.length >= 3) searchRecipients(value);
    else setRecipientOptions([]);
  };

  const handleAttachmentUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setUploadingAttachment(true);
    try {
      const added = await Promise.all(
        files.map(async (file) => {
          if (file.size > 25 * 1024 * 1024) {
            throw new Error(`${file.name} is larger than 25 MB.`);
          }
          if (['.exe', '.jar', '.js'].some((ext) => file.name.toLowerCase().endsWith(ext))) {
            throw new Error(`${file.name} is a file type that can't be attached.`);
          }
          const buffer = await file.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          let binary = '';
          const chunk = 0x8000;
          for (let i = 0; i < bytes.length; i += chunk) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
          }
          return {
            filename: file.name,
            content: btoa(binary),
            content_type: file.type || 'application/octet-stream',
            size: file.size,
          };
        })
      );
      setAttachments((prev) => [...prev, ...added]);
    } catch (error) {
      notify(error.message, 'error');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleOpenAiDialog = () => {
    if (!features.is_ai_email_enabled) return;
    setAiDialogOpen(true);
  };

  const handleGenerateEmail = async () => {
    setGenerating(true);
    setAiError('');
    setAiDialogOpen(false);
    try {
      const response = await axiosInstance.post('/generate/email', {
        context: aiContext,
        signature: signature || undefined,
      });
      const { subject, content } = response.data;
      // The backend appends the signature when one is sent.
      setNewEmail((prev) => ({ ...prev, subject, content }));
      setAiContext('');
    } catch (error) {
      if (error.response) setAiError(errorText(error, 'The draft could not be generated. Please try again.'));
      else setAiError('No response from the server. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  const listPane = (
    <EmailListPane
      title={folderTitle}
      emails={emails}
      loading={loading}
      loadingMore={loadingMore}
      hasMore={!!nextCursor}
      onLoadMore={handleLoadMore}
      selectedId={selectedEmail?.id || null}
      onSelect={handleEmailSelect}
      onEditDraft={handleEditDraft}
      isDraftsFolder={isDraftsFolder}
      isSentFolder={isSentFolder}
      searchQuery={searchQuery}
      onSearchChange={handleSearch}
      onRefresh={handleRefresh}
      error={listError}
      onRetry={handleRefresh}
      onReconnect={handleReconnect}
      headerSlot={
        isCompact ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <Select
                value={currentFolder ? currentMailbox : ''}
                displayEmpty
                onChange={(event) => {
                  const folder = folders.find((item) => item.param === event.target.value);
                  if (folder) handleSelectFolder(folder);
                }}
                inputProps={{ 'aria-label': 'Folder' }}
                renderValue={() => folderTitle}
              >
                {folders.map((folder) => (
                  <MenuItem key={folder.key} value={folder.param} sx={{ pl: 2 + folder.depth * 1.5 }}>
                    {folder.label}
                    {folder.unread > 0 ? ` (${folder.unread})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Compose">
              <IconButton color="primary" onClick={handleCompose} aria-label="Compose">
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null
      }
      sx={isCompact ? { flex: 1 } : { width: 360, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}
    />
  );

  const detailPane = composing ? (
    <ComposePane
      title={composeTitle}
      draft={newEmail}
      onDraftChange={(patch) => setNewEmail((prev) => ({ ...prev, ...patch }))}
      showCc={showCc}
      showBcc={showBcc}
      onToggleCc={() => setShowCc((prev) => !prev)}
      onToggleBcc={() => setShowBcc((prev) => !prev)}
      recipientOptions={recipientOptions}
      recipientsLoading={recipientsLoading}
      onRecipientSearch={handleRecipientSearch}
      attachments={attachments}
      uploadingAttachment={uploadingAttachment}
      onAttach={handleAttachmentUpload}
      onRemoveAttachment={(index) => setAttachments((prev) => prev.filter((_, i) => i !== index))}
      draftId={draftId}
      savingDraft={savingDraft}
      sending={sending}
      onSaveDraft={handleSaveDraft}
      onDiscard={handleDiscard}
      onSend={handleSendEmail}
      aiEnabled={!!features.is_ai_email_enabled}
      licenseLoading={licenseLoading}
      generating={generating}
      aiError={aiError}
      aiDialogOpen={aiDialogOpen}
      aiContext={aiContext}
      onAiContextChange={setAiContext}
      onOpenAiDialog={handleOpenAiDialog}
      onCloseAiDialog={() => setAiDialogOpen(false)}
      onGenerate={handleGenerateEmail}
    />
  ) : selectedEmail ? (
    <EmailReaderPane
      email={selectedEmail}
      onBack={isCompact ? () => setSelectedEmail(null) : null}
      onReply={handleReply}
      onReplyAll={handleReplyAll}
      onForward={handleForward}
      onMarkUnread={() => markAsUnread(selectedEmail.id)}
      onDelete={() => handleDeleteEmail(selectedEmail.id)}
      busy={busy}
      onError={(message) => notify(message, 'error')}
    />
  ) : (
    <ReaderPlaceholder />
  );

  let frameContent;
  if (configState === 'loading') {
    frameContent = <FrameSkeleton />;
  } else if (configState === 'error') {
    frameContent = (
      <InboxLoadError
        message={configError}
        retrying={configChecking}
        onRetry={() => checkEmailConfiguration()}
        onOpenSettings={openEmailSettings}
      />
    );
  } else if (configState === 'not_connected') {
    frameContent = (
      <InboxConnectEmptyState
        providers={connector.providers}
        providersLoading={connector.providersLoading}
        connecting={connector.connecting}
        error={connector.error}
        onConnect={connector.connect}
        onCancel={connector.cancel}
        onOpenSettings={openEmailSettings}
      />
    );
  } else if (isCompact) {
    frameContent = (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {composing || selectedEmail ? (
          <Box sx={{ flex: 1, minHeight: 0 }}>{detailPane}</Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <AccountBadge account={emailConfig} onOpenSettings={openEmailSettings} />
            </Box>
            {listPane}
          </>
        )}
      </Box>
    );
  } else {
    frameContent = (
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
        <InboxSidebar
          account={emailConfig}
          folders={folders}
          foldersLoading={foldersLoading}
          currentMailbox={currentMailbox}
          onSelectFolder={handleSelectFolder}
          onCompose={handleCompose}
          onOpenSettings={openEmailSettings}
        />
        {listPane}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>{detailPane}</Box>
      </Box>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <ThemeProvider theme={theme}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" component="h1" color="text.primary">
              Inbox
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {configState === 'connected' && emailConfig
                ? `Connected to ${emailConfig.email_address}`
                : 'Your email, next to your CRM.'}
            </Typography>
          </Box>
          <Paper
            elevation={0}
            sx={{
              ...frameHeightSx,
              minHeight: 520,
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            {frameContent}
          </Paper>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={snackbar.severity === 'error' ? 8000 : 5000}
            onClose={(event, reason) => reason !== 'clickaway' && setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </ThemeProvider>
      </MDBox>
    </DashboardLayout>
  );
}

export default EmailInbox;
